import { createAsyncThunk } from '@reduxjs/toolkit'
import toCamel from 'lodash-humps'
import {
  createFetchAsyncThunk,
  csrfToken,
  toSnake,
  throwErrorForErrorResponse,
  throwTextErrorForErrorResponse,
  errorHandler,
  ErrorPayload
} from '../common'
import { closeModal } from './pageSlice'
import type { PageState } from './types'

type ThunkConfig = {
  state: { common: { baseUri: string }; page: PageState }
  rejectValue: ErrorPayload
}

type CreatePageParams = {
  navigate: (path: string) => void
  formData: FormData
}

type ModifyPageParams = {
  layoutableId: number | string
  layoutableType?: string
  formData: FormData
}

type PageIdParams = {
  id: number | string
} & Record<string, unknown>

type CopyPageParams = {
  id: number | string
  navigate: (path: string) => void
} & Record<string, unknown>

export const fetchPages = createFetchAsyncThunk('page/fetchPages', 'pages')

export const createPage = createAsyncThunk<unknown, CreatePageParams, ThunkConfig>(
  'page/createPage',
  async ({ navigate, formData }, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common

    return fetch(`${baseUri}pages`, {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: formData
    })
      .then(throwErrorForErrorResponse)
      .then(async (response) => response.json())
      .then(toCamel)
      .then((payload) => {
        const { pageId } = payload as { pageId: string }
        dispatch(closeModal())
        navigate(`${baseUri}pages/${pageId}/articles`)
        return Promise.resolve({})
      })
      .catch(errorHandler(rejectWithValue))
  }
)

export const modifyPage = createAsyncThunk<unknown, ModifyPageParams, ThunkConfig>(
  'page/modifyPage',
  async ({ layoutableId, formData }, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common

    return fetch(`${baseUri}pages/${layoutableId}`, {
      method: 'put',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: formData
    })
      .then(throwErrorForErrorResponse)
      .then(() => {
        const { currentPage, sort, searchCondition } = getState().page
        dispatch(fetchPages({ page: currentPage, sort, search: searchCondition }))
        dispatch(closeModal())
        return Promise.resolve({})
      })
      .then(toCamel)
      .catch(errorHandler(rejectWithValue))
  }
)

export const deletePage = createAsyncThunk<unknown, PageIdParams, ThunkConfig>(
  'page/deletePage',
  async (params, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common
    const { id } = params

    return fetch(`${baseUri}pages/${id}`, {
      method: 'delete',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify(toSnake(params))
    })
      .then(throwErrorForErrorResponse)
      .then(() => {
        const { currentPage, sort } = getState().page
        dispatch(fetchPages({ page: currentPage, sort }))
        return Promise.resolve({})
      })
      .then(toCamel)
      .catch(errorHandler(rejectWithValue))
      .finally(() => {
        dispatch(closeModal())
      })
  }
)

export const fetchLayoutResult = createAsyncThunk<unknown, { id: number | string }, ThunkConfig>(
  'page/layoutResult',
  async ({ id }, { getState, rejectWithValue }) => {
    const { baseUri } = getState().common

    return fetch(`${baseUri}pages/${id}/layout_result`, {
      headers: {
        Accept: 'application/json'
      }
    })
      .then(throwTextErrorForErrorResponse)
      .then((response) => response.json())
      .then(toCamel)
      .catch(errorHandler(rejectWithValue))
  }
)

export const exportLayout = createAsyncThunk<unknown, { id: number | string; fileName: string }, ThunkConfig>(
  'page/exportLayout',
  async ({ id }, { getState, rejectWithValue }) => {
    const { baseUri } = getState().common

    return fetch(`${baseUri}pages/${id}/export_layout.zip`)
      .then(throwErrorForErrorResponse)
      .then((response) => response.blob())
      .catch(errorHandler(rejectWithValue))
  }
)

export const copyPage = createAsyncThunk<unknown, CopyPageParams, ThunkConfig>(
  'page/copyPage',
  async (params, { getState, rejectWithValue, dispatch }) => {
    const { baseUri } = getState().common
    const { navigate, ...bodyParams } = params

    return fetch(`${baseUri}pages/${bodyParams.id}/copy`, {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify(toSnake(bodyParams))
    })
      .then(throwErrorForErrorResponse)
      .then(async (response) => response.json())
      .then(toCamel)
      .then((payload) => {
        const { pageId } = payload as { pageId: string }
        navigate(`${baseUri}pages/${pageId}/articles?copiedSuccessfully=true`)
        dispatch(closeModal())
        return Promise.resolve({})
      })
      .catch(errorHandler(rejectWithValue))
  }
)
